import Num, { BigNumber } from '@flipster/utils/num';
import type { OrderMarginType } from '@flipster/utils/types/base';

import type { PositionType } from '../../shared/tpsl/bunja';
import type { TpslType } from '../state';

/**
 * TP/SL 폼의 순수 유틸 모음 (차트·jotai atom에 의존하지 않아 단위테스트 가능).
 *
 * - 가격 입력 케이스 판정 (`evaluatePriceCase`)
 * - 초기 트리거 가격 선택 (`pickInitialBaseTriggerPrice`)
 * - 제출(Confirm) 활성/비활성 판정 (`computeModalIsDirty`, `isTpslSubmitDisabled`)
 */

// ─────────────────────────────────────────────────────────────────────────────
// 가격 입력 케이스
// ─────────────────────────────────────────────────────────────────────────────

export type PriceInputCase =
  | 'valid'
  | 'empty'
  | 'too-low-open'
  | 'too-high-open'
  | 'too-low-pending'
  | 'too-high-pending'
  | 'liq-too-low'
  | 'liq-too-high';

// Shared TP/SL price validation: both the trigger price and the limit price must
// sit on the correct side of the market (open) or of the parent order's price
// (pending). For SL in Isolated margin, the trigger price must also stay on the
// safe side of the liquidation price — crossing it makes the SL meaningless
// because liquidation fires first (Long: trigger <= liq, Short: trigger >= liq).
// The market-side and liquidation-side regions never overlap, so check ordering
// is safe. Empty inputs resolve to `valid` (Confirm is gated separately by
// dirty / required flags).
export function evaluatePriceCase({
  tpslType,
  positionType,
  marginType,
  value,
  isEmpty,
  side,
  targetPrice,
  liqPrice,
}: {
  tpslType: TpslType;
  positionType: PositionType;
  marginType: OrderMarginType | undefined;
  value: BigNumber;
  isEmpty: boolean;
  side: 'Long' | 'Short';
  targetPrice: BigNumber;
  liqPrice?: BigNumber;
}): PriceInputCase {
  if (isEmpty) return 'valid';
  const tooLow = `too-low-${positionType}` as const;
  const tooHigh = `too-high-${positionType}` as const;
  if (tpslType === 'tp') {
    if (side === 'Long' && value.isLessThanOrEqualTo(targetPrice)) return tooLow;
    if (side === 'Short' && value.isGreaterThanOrEqualTo(targetPrice)) return tooHigh;
  }
  if (tpslType === 'sl') {
    if (side === 'Long' && value.isGreaterThanOrEqualTo(targetPrice)) return tooHigh;
    if (side === 'Short' && value.isLessThanOrEqualTo(targetPrice)) return tooLow;
    if (marginType === 'Isolated' && liqPrice && liqPrice.isGreaterThan(0)) {
      if (side === 'Long' && value.isLessThanOrEqualTo(liqPrice)) return 'liq-too-low';
      if (side === 'Short' && value.isGreaterThanOrEqualTo(liqPrice)) return 'liq-too-high';
    }
  }
  return 'valid';
}

/** 입력 검증 상태. UI(메시지 색/아이콘)와 Confirm 활성 여부를 모두 이 한 값에서 파생한다. */
export type ValidationStatus = 'valid' | 'warning' | 'error';

/**
 * 트리거 가격 입력에 띄울 메시지 종류. 어떤 i18n 문구를 보여줄지의 단일 결정원이며,
 * `null`이면 메시지 없음(=valid). 같은 inputCase라도 모드에 따라 종류가 달라질 수 있다
 * (예: 시장가 교차가 생성 모드에선 'above/below-market' 에러, 수정 모드에선 'immediate-close' 경고).
 */
export type TriggerPriceMessageKind =
  | 'immediate-close' // 경고: 수정 모드의 즉시 청산(시장가 교차)
  | 'above-market' // 에러: 생성 모드, 시장가보다 높은 가격 필요
  | 'below-market' // 에러: 생성 모드, 시장가보다 낮은 가격 필요
  | 'above-order' // 에러: pending, 주문가보다 높은 가격 필요
  | 'below-order' // 에러: pending, 주문가보다 낮은 가격 필요
  | 'liq-too-low' // 에러: 청산가 이하
  | 'liq-too-high'; // 에러: 청산가 이상

/**
 * inputCase(+모드)로 보여줄 메시지 종류를 결정한다. 메시지 렌더와 상태 판정이 모두 이 결과에서 파생된다.
 * `empty`/`valid`는 메시지 없음(null).
 */
export function resolveTriggerPriceMessageKind(params: {
  inputCase: PriceInputCase;
  isUpdateMode: boolean;
}): TriggerPriceMessageKind | null {
  const { inputCase, isUpdateMode } = params;
  switch (inputCase) {
    // 시장가 교차(즉시 청산): 수정 모드는 경고, 생성 모드는 차단 에러(시장가 룰).
    case 'too-low-open':
      return isUpdateMode ? 'immediate-close' : 'above-market';
    case 'too-high-open':
      return isUpdateMode ? 'immediate-close' : 'below-market';
    case 'too-low-pending':
      return 'above-order';
    case 'too-high-pending':
      return 'below-order';
    case 'liq-too-low':
      return 'liq-too-low';
    case 'liq-too-high':
      return 'liq-too-high';
    case 'valid':
    case 'empty':
    default:
      return null;
  }
}

/**
 * 트리거 가격 입력의 검증 상태. 메시지 종류(kind)에서 파생한다.
 * - `valid`: 메시지 없음(빈 입력 포함 — 빈 입력은 dirty/required로 별도 게이팅).
 * - `warning`: 즉시 청산(수정 모드) — 메시지는 띄우되 Confirm은 막지 않는다.
 * - `error`: 그 외 모든 위반(시장가 룰 / 주문가 / 청산가) — Confirm 차단.
 */
export function resolveTriggerPriceStatus(params: {
  inputCase: PriceInputCase;
  isUpdateMode: boolean;
}): ValidationStatus {
  const kind = resolveTriggerPriceMessageKind(params);
  if (kind === null) return 'valid';
  return kind === 'immediate-close' ? 'warning' : 'error';
}

// ─────────────────────────────────────────────────────────────────────────────
// 초기 트리거 가격
// ─────────────────────────────────────────────────────────────────────────────

/**
 * TP/SL 모달의 트리거 가격 입력 초기값을 고른다.
 *
 * - 차트 드래그 가격이 주어짐(>0): 생성/수정 무관하게 드래그 가격으로 prefill.
 *   (Flow A 생성, Flow B loss zone 드롭으로 기존 TP/SL을 그 가격으로 수정하는 경우 모두 해당)
 * - 수정(update) 모드: 기존 선택 주문의 트리거 가격.
 * - 그 외 생성 모드: 진입가/주문가로 폴백.
 */
export function pickInitialBaseTriggerPrice(params: {
  isUpdateMode: boolean;
  selectedTriggerPrice: BigNumber;
  entryOrOrderPrice: BigNumber;
  /** 차트 드래그/드롭으로 결정된 초기 트리거 가격. 주어지면 생성/수정 모드 모두에서 우선한다. */
  draggedTriggerPrice?: number | null;
}): BigNumber {
  const { isUpdateMode, selectedTriggerPrice, entryOrOrderPrice, draggedTriggerPrice } = params;

  if (draggedTriggerPrice != null && draggedTriggerPrice > 0) return Num(draggedTriggerPrice);
  if (isUpdateMode) return selectedTriggerPrice;
  return entryOrOrderPrice;
}

// ─────────────────────────────────────────────────────────────────────────────
// 제출(Confirm) 활성/비활성
// ─────────────────────────────────────────────────────────────────────────────

/** 폼이 변경되었는지(=제출 가능 후보). 차트 드래그/드롭으로 trigger price가 prefill돼도 dirty로 본다. */
export function computeModalIsDirty(flags: {
  /** 차트 드래그/드롭으로 trigger price가 prefill됨 (사용자가 가격을 지정한 것으로 간주). */
  hasDraggedTriggerPrice: boolean;
  priceDirty: boolean;
  qtyDirty: boolean;
  executionDirty: boolean;
}): boolean {
  return flags.hasDraggedTriggerPrice || flags.priceDirty || flags.qtyDirty || flags.executionDirty;
}

/**
 * Confirm 버튼 비활성 여부.
 * - 트리거 가격은 필수값이므로 비어 있으면 항상 차단 — 최우선. 빈 입력은
 *   방향(long/short)·마진 무관하게 유효한 주문이 될 수 없고, evaluatePriceCase는
 *   빈 입력을 valid로 두어 인라인 에러를 띄우지 않으므로(=hasTriggerPriceInputError가
 *   서지 않으므로) 여기서 별도의 required 게이트로 막는다.
 * - 가격 입력 오류(유효하지 않음)는 항상 차단 (서버에 garbage 전송 방지).
 * - 금액 경고만 있으면 "그래도 제출 허용".
 * - 변경이 없으면(dirty 아님) 차단.
 * - 그 외엔 최소수량 오류/전손이면 차단.
 */
export function isTpslSubmitDisabled(flags: {
  triggerPriceInputIsEmpty: boolean;
  hasTriggerPriceInputError: boolean;
  hasLimitPriceInputError: boolean;
  hasTpslAmountWarning: boolean;
  modalIsDirty: boolean;
  hasMinQtyError: boolean;
  isTotalLoss: boolean;
}): boolean {
  if (flags.triggerPriceInputIsEmpty) return true;
  if (flags.hasTriggerPriceInputError || flags.hasLimitPriceInputError) return true;
  if (flags.hasTpslAmountWarning) return false;
  if (!flags.modalIsDirty) return true;
  return flags.hasMinQtyError || flags.isTotalLoss;
}

// ─────────────────────────────────────────────────────────────────────────────
// Partial 수량 prefill
// ─────────────────────────────────────────────────────────────────────────────

/** value 를 quantum(unitOrderQty/tickSize) 배수로 내림(half-down) 정렬한다. */
export function roundHalfDownWithQuantum(value: BigNumber, quantum: BigNumber): BigNumber {
  return value.div(quantum).integerValue(BigNumber.ROUND_HALF_DOWN).times(quantum);
}

/**
 * Partial TP/SL 폼의 Set quantity 초기값.
 *
 * 편집(update) 진입에서는 선택된 주문의 qty 를 그대로 쓴다. 슬라이더 비율에서 역산하면
 * qty → 정수% → qty 왕복에서 소수점이 날아가, 실제 주문과 다른 수량이 노출·전송된다.
 * (QA-21323: Add position 으로 포지션이 10 → 15 BCH 가 되면 5.000 BCH TP 가 33% 로 반올림되고,
 * 15 × 33% = 4.950 BCH 로 표시되던 문제)
 *
 * 단, 주문 qty 가 포지션보다 큰 상태(partial close 로 포지션만 줄어든 100% 초과, QA-21301)는
 * positionQty 로 clamp 한다. 슬라이더가 100% 로 clamp 되므로 숫자를 맞추고, 이 상태에서 열려 있는
 * Confirm(`isTpslSubmitDisabled` 의 amount-warning 우회)을 그대로 눌렀을 때 포지션 전량으로
 * 정상화되게 하기 위함이다.
 *
 * 선택 주문이 없는 신규(add) 진입은 비율(기본 0%) 기반 계산으로 폴백한다. 0 이하도 같은 폴백을
 * 탄다(절댓값을 취하지 않는다). 호출부가 넘기는 `selectedTpslOrder.qty` 자체는 side 와 무관하게
 * 항상 양수지만, 이 값을 읽는 `selectedTpslOrderBunja.tpslOrderQtyAtom` 은 entire 탭에서
 * `OpenPosition.quantity`(= raw `position`, Short 면 음수)로 소스가 갈린다. 여기서 절댓값을 취하면
 * 같은 소스에서 비율을 만드는 `deriveTpslQtyPercent` 가 음수를 0% 로 바닥 처리하는 것과 어긋나
 * "슬라이더 0% / 입력창 0.750" 처럼 두 파생이 갈라진다.
 */
export function resolvePartialQtyPrefill(params: {
  /** 선택된 TP/SL 주문의 qty. 신규(add) 모드면 undefined. */
  selectedOrderQty: BigNumber | string | undefined;
  positionQty: BigNumber;
  /** 표시용 정수 비율(0~100). 신규 모드 폴백 계산에만 쓰인다. */
  qtyPercent: number;
  unitOrderQty: BigNumber;
  tradeQtyPrecision: number;
}): string {
  const { selectedOrderQty, positionQty, qtyPercent, unitOrderQty, tradeQtyPrecision } = params;

  const orderQty = Num(selectedOrderQty || 0);
  if (orderQty.isGreaterThan(0)) {
    const clamped = orderQty.isGreaterThan(positionQty) ? positionQty : orderQty;
    return clamped.toFixed(tradeQtyPrecision);
  }

  return roundHalfDownWithQuantum(positionQty.multipliedBy(qtyPercent).dividedBy(100), unitOrderQty).toFixed(
    tradeQtyPrecision,
  );
}
